﻿import { acceptHMRUpdate, defineStore } from 'pinia'
import type { EventTypeDto } from '@/types'
import { getAllEventTypes, getEventTypeById } from '@/api'
import { reactive } from 'vue'

const EVENT_TYPE_PARTITION_KEY = 'EventType'

export interface UseEventTypeStore {
    eventTypes: EventTypeDto[]

    getEventType(id: number): EventTypeDto | undefined

    loadAll(): Promise<void>

    /** Ensures type metadata is in cache (needed when editing existing events on step 2). */
    ensureEventTypeLoaded(typeId: number): Promise<EventTypeDto | undefined>
}
export const useEventTypeStore = defineStore('eventType', function (): UseEventTypeStore {
    const eventTypes = reactive<EventTypeDto[]>([])

    function getEventType(id: number): EventTypeDto | undefined {
        const normalizedId = Number(id)
        if (!normalizedId) {
            return undefined
        }

        return eventTypes.find((et: EventTypeDto) => Number(et.id) === normalizedId)
    }

    async function loadAll() {
        if (eventTypes.length === 0) {
            try {
                eventTypes.push(...(await getAllEventTypes()))
            } catch (error) {
                console.warn('Failed to load event types:', error)
            }
        }
    }

    async function ensureEventTypeLoaded(typeId: number): Promise<EventTypeDto | undefined> {
        const normalizedId = Number(typeId)
        if (!normalizedId) {
            return undefined
        }

        await loadAll()

        const cached = getEventType(normalizedId)
        if (cached) {
            return cached
        }

        try {
            const fetched = await getEventTypeById(normalizedId, EVENT_TYPE_PARTITION_KEY)
            if (fetched && !eventTypes.some((et) => Number(et.id) === normalizedId)) {
                eventTypes.push(fetched)
            }
            return fetched
        } catch {
            return undefined
        }
    }

    return { eventTypes, getEventType, loadAll, ensureEventTypeLoaded }
})
// make sure to pass the right store definition, `useAuth` in this case.
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useEventTypeStore, import.meta.hot))
}
